import React, { useState, useRef, useEffect, useMemo } from 'react';
import '../styles/components/PlaybackPanel.css';
import '../styles/common/moments.css';
import { getMomentStyling } from '../utils/momentUtils';

interface Speaker {
  id: number;
  name: string;
}

interface Moment {
  id: string;
  name: string;
  time: number;
}

interface TimelineSegment {
  id: string;
  startTime: number;
  endTime: number;
  type: 'agent' | 'customer' | 'silence';
  speaker: Speaker;
}

interface PlaybackPanelProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  audioSrc: string;
  currentTranscriptId?: string;
  onTimeUpdate: (time: number) => void;
  onPlayingSegmentChange: (segmentId: string) => void;
  transcriptTimestamps: { id: string; startTime: number; endTime: number; speaker: Speaker }[];
  moments?: Moment[];
  totalDuration: number;
  currentTimePosition: number;
  setCurrentTimePosition: (time: number) => void;
  /** Fired when the media element cannot load (e.g. 404). Replay can still show transcript/TA. */
  onMediaError?: () => void;
}

const PlaybackPanel: React.FC<PlaybackPanelProps> = ({
  audioRef,
  audioSrc,
  currentTranscriptId,
  onTimeUpdate,
  onPlayingSegmentChange,
  transcriptTimestamps,
  moments = [],
  totalDuration,
  currentTimePosition,
  setCurrentTimePosition,
  onMediaError,
}) => {
  // Core state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(totalDuration || 0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(0.8);
  const [hoverPosition, setHoverPosition] = useState<number | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Refs
  // const audioRef = useRef<HTMLAudioElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Generate timeline segments
  const timelineSegments: TimelineSegment[] = useMemo(() => {
    let segments = [...transcriptTimestamps].map(item => {
      // Determine segment type based on id and speaker
      let type: 'agent' | 'customer' | 'silence';

      if (item.id.startsWith('silence-') || item.id.includes('silence')) {
        type = 'silence';
      }
      else if (
        item.speaker.name.toLowerCase().includes('agent') ||
        item.speaker.id === 0
      ) {
        type = 'agent';
      }
      else {
        type = 'customer';
      }
      
      return {
        id: item.id,
        startTime: item.startTime,
        endTime: item.endTime,
        speaker: item.speaker,
        type
      };
    });
    
    // Add silence segments if none exist
    const silenceSegments = segments.filter(s => s.type === 'silence');
    if (silenceSegments.length === 0) {
      // Sort segments chronologically
      segments.sort((a, b) => a.startTime - b.startTime);
      
      // Find gaps between segments
      const silences: TimelineSegment[] = [];
      for (let i = 0; i < segments.length - 1; i++) {
        const gap = segments[i+1].startTime - segments[i].endTime;
        if (gap > 1000) { // 800ms is wehre we would want to be for voice bots
          silences.push({
            id: `silence-auto-${i}`,
            startTime: segments[i].endTime,
            endTime: segments[i+1].startTime,
            type: 'silence',
            speaker: { id: 999, name: 'Silence' }
          });
        }
      }
      
      // Add silences and re-sort
      segments = [...segments, ...silences].sort((a, b) => a.startTime - b.startTime);
    }
    
    return segments;
  }, [transcriptTimestamps]);

  // Basic audio setup
  useEffect(() => {
    // Set playback rate and volume whenever the audio element is available
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
      audioRef.current.volume = volume;
    }
    console.log("audioRef.current", audioRef.current);
  }, [audioRef.current, playbackRate, volume]);

  // Simple play/pause toggle
  const handlePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play()
        .catch(error => {
          console.error("Error playing audio:", error);
        });
    }
  };

  // Handle timeline clicks
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    console.log('Clicked progress bar at position:', percentage, 'Setting time to:', newTime);

    // Set audio position
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setCurrentTimePosition(newTime);
    console.log('handleProgressClick', newTime);
    onTimeUpdate(newTime * 1000);
  };

  // Handle segment clicks
  const handleSegmentClick = (segment: TimelineSegment) => {
    if (!audioRef.current) return;
    
    const timeInSeconds = segment.startTime / 1000;
    audioRef.current.currentTime = timeInSeconds;
    setCurrentTime(timeInSeconds);
    setCurrentTimePosition(timeInSeconds);
    onTimeUpdate(segment.startTime);
    console.log('handleSegmentClick', segment.startTime);
    console.log('segment.id', segment.id);
    if (!segment.id.startsWith('silence-')) {
      onPlayingSegmentChange(segment.id);
    }
  };

  // Handle moment clicks
  const handleMomentClick = (time: number) => {
    if (!audioRef.current) return;
    
    const timeInSeconds = time / 1000;
    audioRef.current.currentTime = timeInSeconds;
    setCurrentTime(timeInSeconds);
    setCurrentTimePosition(timeInSeconds);
    onTimeUpdate(time);
  };

  // Format time display
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Calculate percentage for positioning
  const getPositionPercentage = (timeMs: number) => {
    return (timeMs / (duration * 1000)) * 100;
  };
  
  // Handle tooltip on hover
  const handleProgressHover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!showTooltip) setShowTooltip(true);
    
    const rect = e.currentTarget.getBoundingClientRect();
    const hoverX = e.clientX - rect.left;
    const percentage = hoverX / rect.width;
    setHoverPosition(percentage * duration);
  };
  
  const handleProgressLeave = () => {
    setShowTooltip(false);
    setHoverPosition(null);
  };

  return (
    <div className="playback-panel">
     
      {/* Timeline container */}
      <div className="master-timeline-container" ref={containerRef}>
        {/* Timeline labels */}
        <div className="timeline-labels-container">
          <div className="timeline-label agent-label">Agent</div>
          <div className="timeline-label customer-label">Customer</div>
        </div>
        
        {/* Play button */}
        <div className="play-control">
          <button 
            className="play-button"
            onClick={handlePlayPause}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? "⏸" : "▶"}
          </button>
        </div>
        
        {/* Timeline */}
        <div className="timeline-wrapper">
          {/* Agent moments */}
          <div className="timeline-layer agent-moments-layer">
            {moments.map((moment, index) => {
              // Check if any previous moments share the same timestamp
              const sameTimeMoments = moments.slice(0, index).filter(m => m.time === moment.time).length;
              // Add a small horizontal offset (20px) for each overlapping moment
              const offset = sameTimeMoments * 20;
              
              const { className: sentimentClass } = getMomentStyling(
                (moment as any).sentiment_id,
                (moment as any).sentiment_name
              );
              
              return (
                <div 
                  key={`moment-${index}`}
                  className="moment-indicator agent-moment"
                  style={{
                    left: `calc(${getPositionPercentage(moment.time)}% + ${offset}px)`,
                    transform: `translateX(-50%)`
                  }}
                  onClick={() => handleMomentClick(moment.time)}
                  title={moment.name}
                >
                  <div className={`moment-tag ${sentimentClass}`}>{moment.name}</div>
                  <div className={`moment-arrow ${sentimentClass}`}></div>
                </div>
              );
            })}
          </div>
          
          {/* Agent timeline */}
          <div className="timeline-layer agent-layer">
            {timelineSegments
              .filter(segment => segment.type === 'agent')
              .map((segment, index) => (
                <div 
                  key={`agent-${index}`}
                  className="timeline-segment agent"
                  style={{
                    left: `${getPositionPercentage(segment.startTime)}%`,
                    width: `${getPositionPercentage(segment.endTime - segment.startTime)}%`
                  }}
                  onClick={() => handleSegmentClick(segment)}
                  title={`${segment.speaker.name}: ${formatTime(segment.startTime/1000)} - ${formatTime(segment.endTime/1000)}`}
                />
              ))}
          </div>
          
          {/* Silence timeline */}
          <div className="timeline-layer silence-layer">
            {timelineSegments
              .filter(segment => segment.type === 'silence')
              .map((segment, index) => (
                <div 
                  key={`silence-${index}`}
                  className="timeline-segment silence"
                  style={{
                    left: `${getPositionPercentage(segment.startTime)}%`,
                    width: `${getPositionPercentage(segment.endTime - segment.startTime)}%`
                  }}
                  onClick={() => handleSegmentClick(segment)}
                  title={`Silence : ${formatTime(segment.startTime/1000)} - ${formatTime(segment.endTime/1000)} Duration: ${segment.endTime - segment.startTime}ms`}
                />
              ))}
          </div>
          
          {/* Customer timeline */}
          <div className="timeline-layer customer-layer">
            {timelineSegments
              .filter(segment => segment.type === 'customer')
              .map((segment, index) => (
                <div 
                  key={`customer-${index}`}
                  className="timeline-segment customer"
                  style={{
                    left: `${getPositionPercentage(segment.startTime)}%`,
                    width: `${getPositionPercentage(segment.endTime - segment.startTime)}%`
                  }}
                  onClick={() => handleSegmentClick(segment)}
                  title={`${segment.speaker.name}: ${formatTime(segment.startTime/1000)} - ${formatTime(segment.endTime/1000)}`}
                />
              ))}
          </div>
          
          {/* Playhead */}
          <div 
            className="timeline-playhead"
            // style={{left: `${(currentTime / duration) * 100}%`}}
            style={{left: `${(currentTimePosition / duration) * 100}%`}}
          />
          
          {/* Progress layer */}
          <div 
            className="audio-progress-layer" 
            onClick={handleProgressClick}
            onMouseMove={handleProgressHover}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={handleProgressLeave}
          >
            <div 
              className="audio-progress-fill"
              // style={{width: `${(currentTime / duration) * 100}%`}}
              style={{width: `${(currentTimePosition / duration) * 100}%`}}
            />
            
            {/* Tooltip */}
            {showTooltip && hoverPosition !== null && (
              <div 
                className="time-tooltip"
                style={{left: `${(hoverPosition / duration) * 100}%`}}
              >
                {formatTime(hoverPosition)}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Controls */}
      <div className="bottom-controls">
        <div className="playback-rate">
          <button 
            className={`rate-button ${playbackRate === 0.5 ? 'active' : ''}`}
            onClick={() => setPlaybackRate(0.5)}
          >
            0.5x
          </button>
          <button 
            className={`rate-button ${playbackRate === 1 ? 'active' : ''}`}
            onClick={() => setPlaybackRate(1)}
          >
            1x
          </button>
          <button 
            className={`rate-button ${playbackRate === 1.5 ? 'active' : ''}`}
            onClick={() => setPlaybackRate(1.5)}
          >
            1.5x
          </button>
          <button 
            className={`rate-button ${playbackRate === 2 ? 'active' : ''}`}
            onClick={() => setPlaybackRate(2)}
          >
            2x
          </button>
        </div>
        
        <div className="time-display">
          {/* {formatTime(currentTime)} / {formatTime(duration)} */}
          {formatTime(currentTimePosition)} / {formatTime(duration)}
        </div>
        
        <div className="volume-control">
          <label className="volume-label">Volume: {Math.round(volume * 100)}%</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="volume-slider"
            aria-label="Volume control"
          />
        </div>
      </div>
      
      {/* Add a test button to verify audio functionality */}
      {/* <div className="audio-container" style={{margin: "10px 0"}}> */}
      <div className="audio-container">
        {/* Visible but minimal audio element */}
        <audio 
          ref={audioRef}
          src={audioSrc}
          preload="auto"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onClick={(e) => {
            const audio = e.currentTarget;
            console.log("OnClick: event type from audio element", e.type);
            console.log('OnClick: onTimeUpdate from audio element', audio.currentTime);
            console.trace();
          }}
          onTimeUpdate={(e) => {
            console.log("onTimeUpdate: src", audioSrc);
            const audio = e.currentTarget;
            console.log("onTimeUpdate: audio.currentTime", audio.currentTime);
            setCurrentTimePosition(audio.currentTime);
            setCurrentTime(audio.currentTime);
            console.log("onTimeUpdate: currentTime", currentTime);
            console.log("onTimeUpdate: currentTimePosition", currentTimePosition);
            console.log("onTimeUpdate: audio.currentTime", audio.currentTime);
            onTimeUpdate(audio.currentTime * 1000);
          }}
          onError={() => {
            onMediaError?.();
          }}
          onLoadedMetadata={(e) => {
            const audio = e.currentTarget;
            if (audio.duration && !isNaN(audio.duration)) {
              setDuration(audio.duration);
            }
          }}
          onEnded={() => {
            setIsPlaying(false);
            setCurrentTime(0);
            setCurrentTimePosition(0);
          }}
          style={{width: "100%", height: "32px"}}
        />
      </div>
    </div>
  );
};

export default PlaybackPanel; 